import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Header component
 *
 * - Handles scroll‑based styling.
 * - Closes mobile menu on route change.
 * - Provides robust cleanup for event listeners (SSR safe).
 */
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  /** ---------------------------------------------------------------
   *  Scroll listener – adds a small robustness guard for environments
   *  where `window` is not defined (e.g., Server‑Side Rendering).
   *  --------------------------------------------------------------- */
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleScroll = () => {
      // Guard against unexpected values
      const scrollY = window.scrollY ?? 0;
      setIsScrolled(scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    // Initialise state in case the page is already scrolled
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  /** ---------------------------------------------------------------
   *  Close mobile menu when navigation occurs.
   *  --------------------------------------------------------------- */
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Success Stories', path: '/success-stories' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <>
      {/* ----------------------------------------------------------- */}
      {/* Header */}
      {/* ----------------------------------------------------------- */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="w-full px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3 group">
              <img
                src="https://static.readdy.ai/image/2bdd6ff17b3d38a4f0579550da7234da/bdf4bf99860934c329c77fa4595e712c.png"
                alt="ThriveEdge Logo"
                className="h-12 w-12 object-contain transition-transform duration-300 group-hover:scale-105"
              />
              <span
                className={`font-serif font-bold text-2xl transition-colors duration-300 ${
                  isScrolled ? 'text-primary-charcoal' : 'text-white'
                }`}
              >
                ThriveEdge
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`relative font-sans font-medium text-base transition-colors duration-300 ${
                    location.pathname === link.path
                      ? 'text-primary-gold'
                      : isScrolled
                      ? 'text-primary-charcoal hover:text-primary-gold'
                      : 'text-white hover:text-primary-gold'
                  }`}
                >
                  {link.name}
                  {location.pathname === link.path && (
                    <motion.div
                      layoutId="underline"
                      className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary-gold"
                    />
                  )}
                </Link>
              ))}
            </nav>

            {/* Contact Icons – Desktop */}
            <div className="hidden lg:flex items-center space-x-4">
              <a
                href="mailto:titilayoajayi923@gmail.com"
                className={`w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300 cursor-pointer ${
                  isScrolled
                    ? 'bg-primary-beige text-primary-charcoal hover:bg-primary-sage hover:text-white'
                    : 'bg-white/20 text-white hover:bg-primary-sage'
                }`}
                aria-label="Email"
              >
                <i className="ri-mail-line text-lg" />
              </a>
              <a
                href="tel:+2349060814354"
                className={`w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300 cursor-pointer ${
                  isScrolled
                    ? 'bg-primary-beige text-primary-charcoal hover:bg-primary-sage hover:text-white'
                    : 'bg-white/20 text-white hover:bg-primary-sage'
                }`}
                aria-label="Phone"
              >
                <i className="ri-phone-line text-lg" />
              </a>
              <a
                href="https://wa.me/2349047390448"
                target="_blank"
                rel="noopener noreferrer"
                className={`w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300 cursor-pointer ${
                  isScrolled
                    ? 'bg-primary-beige text-primary-charcoal hover:bg-primary-sage hover:text-white'
                    : 'bg-white/20 text-white hover:bg-primary-sage'
                }`}
                aria-label="WhatsApp"
              >
                <i className="ri-whatsapp-line text-lg" />
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen((prev) => !prev)}
              className={`lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${
                isScrolled ? 'text-primary-charcoal' : 'text-white'
              }`}
              aria-label="Toggle menu"
            >
              <i className={`text-2xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
            </button>
          </div>
        </div>
      </header>

      {/* ----------------------------------------------------------- */}
      {/* Mobile Menu */}
      {/* ----------------------------------------------------------- */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-20 left-0 right-0 bg-white shadow-lg z-40 lg:hidden"
          >
            <nav className="flex flex-col px-6 py-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`py-3 font-sans font-medium text-base border-b border-gray-100 transition-colors ${
                    location.pathname === link.path
                      ? 'text-primary-gold'
                      : 'text-primary-charcoal hover:text-primary-gold'
                  }`}
                >
                  {link.name}
                </Link>
              ))}

              {/* Mobile Contact Icons */}
              <div className="flex items-center space-x-4 mt-4 pt-4 border-t border-gray-100">
                <a
                  href="mailto:titilayoajayi923@gmail.com"
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-beige text-primary-charcoal hover:bg-primary-sage hover:text-white transition-all cursor-pointer"
                  aria-label="Email"
                >
                  <i className="ri-mail-line text-lg" />
                </a>
                <a
                  href="tel:+2349060814354"
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-beige text-primary-charcoal hover:bg-primary-sage hover:text-white transition-all cursor-pointer"
                  aria-label="Phone"
                >
                  <i className="ri-phone-line text-lg" />
                </a>
                <a
                  href="https://wa.me/2349047390448"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-beige text-primary-charcoal hover:bg-primary-sage hover:text-white transition-all cursor-pointer"
                  aria-label="WhatsApp"
                >
                  <i className="ri-whatsapp-line text-lg" />
                </a>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
